import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Globe, BookOpen, Award, Clock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

const features = [
  {
    id: 1,
    icon: Clock,
    title: 'Learn Anytime, Anywhere',
    description: 'Access our flexible training programs - online, classroom, or on-site. Study at your convenience and grow your career globally.'
  },
  {
    id: 2,
    icon: BookOpen,
    title: 'Industry-Relevant Courses',
    description: 'Courses designed to meet the latest global standards and industry needs. Stay ahead with training tailored for today\'s challenges.'
  },
  {
    id: 3,
    icon: Award,
    title: 'Worldwide Recognition',
    description: 'Earn certifications trusted by professionals and companies worldwide. Boost your credibility with ISDE Academy credentials.'
  },
  {
    id: 4,
    icon: Globe,
    title: 'Global Network',
    description: 'Join a community of 1000+ professionals across Manufacturing, IT, and Educational sectors. Network and grow together.'
  }
];

export const EmpowerSuccess = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <section ref={ref} className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white relative overflow-hidden">
      {/* Decorative Elements */}
      <motion.div
        className="absolute top-10 right-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl opacity-20"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.1, 0.2]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 space-y-4"
        >
          <h2 className="text-4xl lg:text-5xl font-bold">
            Empower Your Success with ISDE Academy
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Discover comprehensive training programs designed to elevate your skills, boost productivity, and ensure compliance with industry standards
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <motion.div key={feature.id} variants={cardVariants}>
                <Card className="group h-full bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all duration-300 cursor-pointer">
                  <CardContent className="pt-8 pb-6 text-center">
                    <motion.div
                      className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-white/30 transition-all duration-300"
                      whileHover={{ scale: 1.1, rotate: 10 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Icon className="h-8 w-8 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-bold mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-blue-100 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
